import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { parseTransactionDate } from "../transactions/schema";
import { Alert, Button } from "../../ui";
import { exportMembers } from "./api";
import { saveBlob } from "../workspace/behavior";
import { useAdminApplication, useAdminSession } from "../../app/providers";
import type { Schemas } from "../workspace/api.generated";
import { useLocation, useSearchParams } from "react-router";
import { useAdminNavigation, useRestoreAdminListViewport } from "../../app/navigation/useAdminNavigation";
import { parseAdminListSearch, pathForAdminEntityScreen } from "../../app/routes";
import { useAdminUiStore } from "../../app/providers";
import { MemberListView } from "./components/MemberListView";
import type { MemberSearchFormValues } from "./schema";
import { useMemberList } from "./queries";
import { parseMemberStatus } from "./schema";

function memberSearchParams(options: {
  readonly page: number;
  readonly size: number;
  readonly status: string;
  readonly from?: string;
  readonly to?: string;
  readonly channelCode?: string;
  readonly planCode?: string;
}): URLSearchParams {
  const params = new URLSearchParams({
    page: String(options.page),
    size: String(options.size),
  });
  if (options.status) params.set("status", options.status);
  for (const key of ["from", "to", "channelCode", "planCode"] as const) if (options[key]) params.set(key, options[key]!);
  return params;
}

export function MemberListPage() {
  const location = useLocation();
  const { apiClient } = useAdminApplication();
  const session = useAdminSession();
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const remembered = useAdminUiStore((state) => state.listContexts.members);
  const rememberListContext = useAdminUiStore((state) => state.rememberListContext);
  const list = parseAdminListSearch(searchParams, { page: 0, size: 10 });
  const status = parseMemberStatus(searchParams.get("status"));
  const [keyword, setKeyword] = useState(() => remembered?.filters.keyword ?? "");
  const extra = { from: parseTransactionDate(searchParams.get("from")), to: parseTransactionDate(searchParams.get("to")), channelCode: searchParams.get("channelCode") ?? "", planCode: searchParams.get("planCode") ?? "" };
  const queryInput = { keyword, status, ...extra, page: list.page, size: list.size };
  const options = useQuery({ queryKey: ["admin", session.sessionEpoch, "member-filter-options"], queryFn: async ({ signal }) => {
    const { data } = await apiClient.get<Schemas["MemberSearchOptions"]>("/api/v1/admin/members/search-options", { signal, cache: "no-store" });
    return { channels: data.channels.map(r => ({ value: r.code, label: r.name })), plans: data.plans.map(r => ({ value: r.code, label: r.name })) };
  } });
  const download = async () => {
    if (exporting || !session.authSession?.authorities?.includes("MEMBER_READ")) return;
    setExporting(true); setExportError("");
    try { saveBlob(await exportMembers(apiClient, queryInput), "members.xlsx"); }
    catch { setExportError("검색 조건에 맞는 회원 정보를 다운로드하지 못했습니다. 잠시 후 다시 시도해 주세요."); }
    finally { setExporting(false); }
  };
  const query = useMemberList(queryInput);
  const lastPage = Math.max(0, (query.data?.totalPages ?? 1) - 1);
  const pageOutOfRange = Boolean(query.data && list.page > lastPage);
  const navigation = useAdminNavigation({
    contextKey: "members",
    defaultListPath: "/members?page=0&size=10",
    originLabel: "회원 목록",
  });
  const restorationNotice = useRestoreAdminListViewport("members", Boolean(query.data && !pageOutOfRange));

  const remember = (next: MemberSearchFormValues & { page: number; size: number }, preserveViewport = false) => {
    const params = memberSearchParams(next);
    rememberListContext("members", {
      path: `${location.pathname}?${params.toString()}`,
      page: next.page,
      filters: {
        keyword: next.keyword,
        status: next.status,
        from: next.from ?? "", to: next.to ?? "", channelCode: next.channelCode ?? "", planCode: next.planCode ?? "",
        size: String(next.size),
      },
    }, preserveViewport);
  };

  useEffect(() => {
    const canonical = memberSearchParams({ page: list.page, size: list.size, status, ...extra });
    if (canonical.toString() !== searchParams.toString()) {
      setSearchParams(canonical, { replace: true });
    }
  }, [list.page, list.size, searchParams, setSearchParams, status, extra.from, extra.to, extra.channelCode, extra.planCode]);

  useEffect(() => {
    if (!pageOutOfRange) return;
    const next = { page: lastPage, size: list.size, status, keyword, ...extra };
    remember(next, true);
    setSearchParams(memberSearchParams(next), { replace: true });
  }, [keyword, lastPage, list.size, pageOutOfRange, setSearchParams, status]);

  const applySearch = (filters: MemberSearchFormValues) => {
    const next = { page: 0, size: list.size, ...filters };
    setKeyword(filters.keyword);
    remember(next);
    setSearchParams(memberSearchParams(next));
    navigation.focusListResults();
  };

  const changePage = (page: number) => {
    const next = { page, size: list.size, status, keyword, ...extra };
    remember(next);
    setSearchParams(memberSearchParams(next));
    navigation.focusListResults();
  };

  const viewMember = (memberId: number) => {
    remember({ page: list.page, size: list.size, status, keyword, ...extra });
    navigation.openDetail(pathForAdminEntityScreen("06", memberId), `member:${memberId}:detail`);
  };

  return (
    <>
    {options.isError ? <Alert persistent variant="warning" title="검색 옵션 조회 실패" action={<Button variant="quiet" size="small" onClick={()=>{void options.refetch();}}>다시 조회</Button>}>가입채널과 요금제 목록을 불러오지 못했습니다.</Alert> : null}
    <MemberListView
      filters={{ keyword, status, ...extra }}
      channels={options.data?.channels}
      plans={options.data?.plans}
      onExport={() => { void download(); }}
      exporting={exporting}
      exportError={exportError}
      exportAllowed={Boolean(session.authSession?.authorities?.includes("MEMBER_READ"))}
      result={pageOutOfRange ? undefined : query.data}
      state={query.isError ? "error" : query.isPending || pageOutOfRange ? "loading" : "ready"}
      refreshing={query.isFetching && !query.isPending}
      notice={restorationNotice}
      detailHref={(memberId) => pathForAdminEntityScreen("06", memberId)}
      onSearch={applySearch}
      onPageChange={changePage}
      onRetry={() => { void query.refetch(); }}
      onViewMember={viewMember}
    />
    </>
  );
}
